<template>
  <section class="flex flex-col md-1:flex-row gap-32">
    <poppins-card customClasses="p-[24px] flex flex-col md-1:flex-row">
      <div id="basic-criminal-box">
        <BGCCardContent
          header="Basic Criminal"
          :displayPrice="displayPrices.BASIC.display"
          :strikethroughPrice="displayPrices.BASIC.strikethrough"
          :badge="getBadge('basic')"
          :checklistItems="basicCriminalItems"
          :isLoadingPrices="isLoadingPrices"
          @learnMore="openWhatsCoveredPopup('basic-criminal')"
        />
      </div>
      <hr class="border-grey-divider-subdued my-16 block md-1:hidden" />
      <div id="enhanced-criminal-box">
        <BGCCardContent
          header="Enhanced Criminal"
          :displayPrice="displayPrices.ENHANCED.display"
          :strikethroughPrice="displayPrices.ENHANCED.strikethrough"
          :badge="getBadge('enhanced')"
          :checklistItems="enhancedCriminalItems"
          :isLoadingPrices="isLoadingPrices"
          @learnMore="openWhatsCoveredPopup('enhanced-criminal')"
        />
      </div>
    </poppins-card>
    <poppins-card customClasses="p-[24px] md-1:max-w-[32%]">
      <BGCCardContent
        header="Motor Vehicle Records"
        :displayPrice="displayPrices.MOTOR.display"
        :strikethroughPrice="displayPrices.MOTOR.strikethrough"
        :badge="getBadge('motor')"
        :checklistItems="motorVehicleItems"
        :isLoadingPrices="isLoadingPrices"
        @learnMore="openWhatsCoveredPopup('motor-vehicle-records')"
      />
    </poppins-card>
  </section>
</template>

<script lang="ts">
import Card from '@/components/card/Card.vue';
import BackgroundCheckBadge from '@/graphics/badges/BackgroundCheckBadge.vue';
import EnhancedBackgroundCheckBadge from '@/graphics/badges/EnhancedBackgroundCheckBadge.vue';
import MotorVehicleRecordBadge from '@/graphics/badges/MotorVehicleRecordBadge.vue';
import BGCCardContent from './BGCCardContent.vue';

interface BackgroundCheckPricing {
  checkType: string;
  listPrice: number;
  memberPrice: number;
}

export default {
  name: 'BGCCards',
  components: {
    PoppinsCard: Card,
    BGCCardContent,
  },
  props: {
    bgcPricing: {
      type: Array as () => BackgroundCheckPricing[],
      required: false,
      default: null,
    },
    isLoadingPrices: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    basicCriminalItems() {
      return [
        'Social security trace and address history',
        'National Criminal Search',
        'Sex Offender Registry Search',
        'Global Watchlist Search',
        'Current County Criminal Search',
      ];
    },
    enhancedCriminalItems() {
      return [...this.basicCriminalItems, 'Unlimited County Criminal Search', 'Federal Criminal Records'];
    },
    motorVehicleItems() {
      return [
        'Full name',
        'Current license status, current license type, and current license class type',
        'Restrictions, suspensions, violations, and clerical updates',
      ];
    },
    displayPrices() {
      const prices = {
        BASIC: { display: '$25', strikethrough: null },
        ENHANCED: { display: '$75', strikethrough: null },
        MOTOR: { display: '$38', strikethrough: null },
      } as any;
      if (this.bgcPricing) {
        this.bgcPricing.forEach((check) => {
          prices[check.checkType].display = `$${check.memberPrice / 100}`;
          if (check.memberPrice < check.listPrice) prices[check.checkType].strikethrough = `$${check.listPrice / 100}`;
        });
      }
      return prices;
    },
  },
  methods: {
    getBadge(type) {
      if (type === 'basic') return BackgroundCheckBadge;
      if (type === 'enhanced') return EnhancedBackgroundCheckBadge;
      if (type === 'motor') return MotorVehicleRecordBadge;
    },
    openWhatsCoveredPopup(anchor) {
      window.open(
        `https://www.sittercity.com/background-checks/whats-covered#${anchor}`,
        'popupWindow',
        'width=800,height=600,scrollbars=yes,resizable=yes'
      );
    },
  },
};
</script>

<style lang="scss" scoped>
#basic-criminal-box {
  @media (min-width: 768px) {
    border-right: 2px solid theme('colors.grey.divider.subdued');
    padding-right: 24px;
    width: 50%;
  }
}

#enhanced-criminal-box {
  @media (min-width: 768px) {
    padding-left: 24px;
    width: 50%;
  }
}
</style>
